import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { CookieConsent } from "@/components/site/CookieConsent";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// HomePage stays eager so the landing route renders instantly.
import HomePage from "@/pages/HomePage";

// Everything else is code-split so the initial bundle stays small. Heavy,
// rarely-needed code (admin dashboard, map editor, html5-qrcode scanner,
// recharts) only downloads when the matching route is visited.
const TicketsPage = lazy(() => import("@/pages/TicketsPage"));
const BoothsPage = lazy(() => import("@/pages/BoothsPage"));
const HallenplanPage = lazy(() => import("@/pages/HallenplanPage"));
const ExhibitorsPage = lazy(() => import("@/pages/ExhibitorsPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const SuccessPage = lazy(() => import("@/pages/SuccessPage"));
const CancelPage = lazy(() => import("@/pages/CancelPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const DoorScannerPage = lazy(() => import("@/pages/DoorScannerPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("@/pages/DatenschutzPage"));
const AGBPage = lazy(() => import("@/pages/AGBPage"));
const AccountLoginPage = lazy(() => import("@/pages/AccountLoginPage"));
const AccountDashboardPage = lazy(() => import("@/pages/AccountDashboardPage"));
const AccountForgotPasswordPage = lazy(() => import("@/pages/AccountForgotPasswordPage"));
const AccountSetPasswordPage = lazy(() => import("@/pages/AccountSetPasswordPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const PageLoader = () => (
  <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-testid="page-loader">
    <div className="w-8 h-8 border-2 border-[#FFCC51]/30 border-t-[#FFCC51] rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <LanguageProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/tickets" element={<TicketsPage />} />
                <Route path="/booths" element={<BoothsPage />} />
                <Route path="/hallenplan" element={<HallenplanPage />} />
                <Route path="/aussteller" element={<ExhibitorsPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/kontakt" element={<ContactPage />} />
                <Route path="/success" element={<SuccessPage />} />
                <Route path="/cancel" element={<CancelPage />} />
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/einlass" element={<DoorScannerPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/datenschutz" element={<DatenschutzPage />} />
                <Route path="/agb" element={<AGBPage />} />
                <Route path="/account/login" element={<AccountLoginPage />} />
                <Route path="/account/forgot-password" element={<AccountForgotPasswordPage />} />
                <Route path="/account/set-password" element={<AccountSetPasswordPage />} />
                <Route path="/account" element={<AccountDashboardPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <CookieConsent />
            <Toaster position="top-right" theme="dark" />
          </ErrorBoundary>
        </BrowserRouter>
      </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
